import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';

export function RolesPanel({ roles, openNewRole, openEditRole, deleteRole }) {
  return (
    <Card className="rounded-[1.5rem] border-blue-100 bg-white shadow-sm sm:rounded-md sm:border-zinc-200" data-testid="roles-panel">
      <CardContent className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-2xl font-black text-zinc-950 font-heading sm:text-base sm:font-bold">Roles & Permissions</h2>
          <p className="text-sm font-semibold text-zinc-600 sm:text-xs sm:font-normal sm:text-zinc-500">Edit built-in role permissions or create custom roles for exact access control.</p>
          </div>
          <Button type="button" className="h-12 rounded-2xl bg-blue-600 font-black text-white hover:bg-blue-700 sm:h-9 sm:rounded-sm" onClick={openNewRole} data-testid="roles-panel-create-button"><Plus className="mr-1 h-4 w-4" /> Role</Button>
        </div>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="roles-list">
          {roles.map((role) => <RoleCard key={role.slug} role={role} openEditRole={openEditRole} deleteRole={deleteRole} />)}
        </div>
      </CardContent>
    </Card>
  );
}

function RoleCard({ role, openEditRole, deleteRole }) {
  return (
    <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:rounded-md sm:bg-zinc-50 sm:border-zinc-200 sm:p-3" data-testid={`role-card-${role.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-lg font-black capitalize text-zinc-950 sm:text-sm sm:font-semibold" data-testid={`role-name-${role.slug}`}>{role.name}</p>
          <p className="text-sm font-semibold text-zinc-600 sm:text-xs sm:font-normal sm:text-zinc-500">{role.is_system ? 'Built-in role · permissions editable' : role.description || 'Custom role'}</p>
        </div>
        <Badge variant="secondary" className="text-[10px] font-black bg-white text-blue-700">{role.is_system ? 'System' : 'Custom'}</Badge>
      </div>
      <p className="mt-2 text-sm font-black text-blue-700 sm:text-xs sm:text-zinc-600" data-testid={`role-resource-count-${role.slug}`}>{Object.keys(role.permissions || {}).length} resources</p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-2 sm:flex sm:gap-1"><Button type="button" className="h-12 rounded-2xl bg-blue-600 font-black text-white hover:bg-blue-700 sm:h-8 sm:rounded-sm" onClick={() => openEditRole(role)} data-testid={`edit-role-${role.slug}`}>Edit</Button>{!role.is_system && <Button type="button" variant="outline" className="h-12 rounded-2xl border-red-100 bg-red-50 font-black text-red-600 sm:h-8 sm:rounded-sm" onClick={() => deleteRole(role.slug)} data-testid={`delete-role-${role.slug}`}>Archive</Button>}</div>
    </div>
  );
}

export function RoleBuilderDialog({ open, setOpen, editingRole, error, roleForm, setRoleForm, permissionResources, togglePermission, saveRole }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto rounded-[1.5rem] bg-[#eef7ff] p-4 sm:bg-white sm:p-6">
        <DialogHeader className="rounded-[1.5rem] bg-gradient-to-br from-[#2563EB] via-[#0EA5E9] to-[#14B8A6] p-4 text-white sm:bg-none sm:p-0 sm:text-zinc-950">
          <DialogTitle className="text-2xl font-black font-heading">{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
          <DialogDescription className="font-semibold text-blue-50 sm:text-zinc-500">Select resource access for this role. Admin keeps critical team/settings access to prevent lockout.</DialogDescription>
        </DialogHeader>
        {error && <p className="rounded-2xl bg-red-50 p-3 text-sm font-semibold text-red-600" data-testid="role-builder-error">{error}</p>}
        <form onSubmit={saveRole} className="space-y-4" data-testid="role-builder-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Role Name *</Label><Input className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} required data-testid="role-name-input" /></div>
            <div><Label>Description</Label><Input className="h-12 rounded-2xl border-blue-100 text-base font-semibold sm:h-9 sm:rounded-sm sm:text-sm" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} data-testid="role-description-input" /></div>
          </div>
          <PermissionGrid permissionResources={permissionResources} roleForm={roleForm} togglePermission={togglePermission} />
          <DialogFooter>
            <Button type="button" variant="outline" className="h-12 rounded-2xl sm:h-9 sm:rounded-sm" onClick={() => setOpen(false)} data-testid="role-cancel-button">Cancel</Button>
            <Button type="submit" className="h-12 rounded-2xl bg-blue-600 font-black text-white hover:bg-blue-700 sm:h-9 sm:rounded-sm" data-testid="role-save-button">Save Role</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PermissionGrid({ permissionResources, roleForm, togglePermission }) {
  return (
    <div className="space-y-2" data-testid="role-permissions-grid">
      {permissionResources.map((resource) => (
        <div key={resource.key} className="rounded-2xl border border-blue-100 bg-white p-3 shadow-sm sm:rounded-md sm:border-zinc-200" data-testid={`role-resource-${resource.key}`}>
          <p className="mb-2 text-base font-black capitalize text-zinc-950 sm:text-sm sm:font-bold">{resource.label}</p>
          <div className="flex flex-wrap gap-2">
            {resource.actions.map((action) => (
              <label key={action} className="inline-flex min-h-10 items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-black capitalize text-blue-800 sm:min-h-7 sm:rounded-md sm:text-xs sm:font-medium" data-testid={`role-permission-${resource.key}-${action}`}>
                <input type="checkbox" checked={(roleForm.permissions?.[resource.key] || []).includes(action)} onChange={() => togglePermission(resource.key, action)} data-testid={`role-permission-checkbox-${resource.key}-${action}`} />
                {action.replace('_', ' ')}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}